<script lang="ts" setup>
import { computed, defineExpose, defineProps, onMounted, ref, withDefaults } from "vue";
import { appContext } from "@/appContext";
import * as dropIn from 'braintree-web-drop-in';
import { Dropin } from 'braintree-web-drop-in';

export interface IPaymentFormData {
  type: 'card' | 'new'
  data: {
    token: string
    method: string
  } | {
    card_id: string
  }
}

interface IUserPaymentCard {
  uuid: string;
  card_brand: string;
  card_last_four_digits: string;
}

const props = withDefaults(
  defineProps<{
    overUserData?: any,
    userCards?: IUserPaymentCard[],
  }>(), {
    overUserData: null,
    userCards: () => [],
  }
);

onMounted(() => {
  createBrainTreeDropInClient();

  console.log(props);
});

function createBrainTreeDropInClient() {
  const googlePay = {
    merchantId: null,
    environment: null,
    transactionInfo: {
      totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
      currencyCode: 'USD'
    },
    cardRequirements: {
      billingAddressRequired: false
    }
  };

  const applePay = {
    displayName: 'BlueCrates LLC',
    paymentRequest: {
      total: {
        label: 'BlueCrates LLC',
        amount: '0',
        type: 'pending'
      }
    }
  };

  if (appContext.APP_ENVIRONMENT() === 'production') {
    googlePay.merchantId = appContext.GOOGLE_MERCHANT_ID() as any;
  } else {
    googlePay.environment = 'TEST' as any;
  }

  let options = {
    container: '#drop-container',
    authorization: brainTreeToken.value,
    customerId: userData.value ? userData.value.braintree : null,
    locale: 'en_US',
    paypal: {
      // TODO: ???
      flow: 'vault'
    },
    googlePay,
    applePay
  };

  dropIn.create(options as any, (error, instance) => {
    if (error) {
      console.error("Braintree error", error);

      braintreeError.value = (error as Error).message;
    } else {
      braintreeInstance.value = instance;
    }
  });
}

let selectedUserCardId = ref<string | null>(null);

let braintreeError = ref('');
let braintreeInstance = ref<Dropin | null | undefined>(null);

let userData = computed<any>(() => {
  return props.overUserData as any;
});

let brainTreeToken = computed(() => {
  return appContext.BRAINTREE_TOKEN();
});

function requestPaymentMethod(): Promise<IPaymentFormData> {
  if (!braintreeInstance.value)
    throw 'Payment gateway is not initialized.';

  return new Promise<IPaymentFormData>((resolve, reject) => {
    if (selectedUserCardId.value) {
      resolve({
        type: "card",
        data: {
          card_id: selectedUserCardId.value
        }
      } as IPaymentFormData);
    } else {
      braintreeInstance.value.requestPaymentMethod({}, (error, payload) => {
        // TODO: Check it
        if (error) {
          reject(error);
        }

        resolve({
          type: "new",
          data: {
            method: payload.method,
            token: payload.nonce
          }
        });
      });
    }
  });
}

defineExpose({
  requestPaymentMethod
});

</script>

<template>
  <div class="checkout-summary cursor-default">
    <div class="text-muted mb-3 cursor-default">
      Choose a way to pay
    </div>

    <div class="checkout-summary__section">
      <div v-if="props.userCards.length">
        <div class="mb-3">
          <b-radio-group v-model="selectedUserCardId">
            <div class="list-group">
              <div
                v-for="i in props.userCards"
                :key="i.uuid"
                class="list-group-item"
              >
                <b-form-radio :value="i.uuid">
                  {{ i.card_brand }} *{{ i.card_last_four_digits }}
                </b-form-radio>
              </div>

              <div class="list-group-item">
                <b-form-radio :value="null">
                  NEW PAYMENT METHOD
                </b-form-radio>
              </div>
            </div>
          </b-radio-group>
        </div>
      </div>

      <div v-else>
        <div class="font-weight-bold mb-3 cursor-default">
          Choose a way to pay
        </div>
      </div>

      <div v-show="selectedUserCardId === null">
        <div id="drop-container"/>
      </div>

      <small
        v-if="braintreeError"
        class="error"
      >
        {{ braintreeError }}
      </small>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~styles/globals";

.checkout-summary {
  .heading-4 {
    margin-bottom: 2rem;
  }

  &__section {
    padding: 1.5rem 0;
    padding-top: 0;

    border-bottom: 1px solid rgba(#c8cfd8, 0.5);

    &:last-child {
      border-bottom: 0;
      padding-bottom: 0;

      .paragraph-small {
        margin-top: 0.5rem;
      }
    }
  }
}

.error {
  color: $dark-red;
}

#drop-container {
  width: 100%;
}

.braintree-placeholder {
  display: none;
}
</style>
